import React, { useEffect, useMemo, useState } from 'react';
import {
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  type MRT_ColumnDef,
  useMaterialReactTable,
  MaterialReactTable,
} from 'material-react-table';
import { Box } from '@mui/material';
import { useGetAllMonitorProgettiNodi } from './Services';
import { MonitorProgettiNodi } from './types';
import ListaMonitorProgettiVersOreInline from "./ListaMonitorProgettiVersOreInline"

interface Props {
  codprogprop: string;
}

const ListaMonitorProgettiNodiInline: React.FC<Props> = ({ codprogprop }) => {
  const [selectedRow, setSelectedRow] = useState<MonitorProgettiNodi | null>(null);

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });

  useEffect(() => {
    if (codprogprop) {
      setColumnFilters([{id: 'CODPROG', value: codprogprop}]);
    }
  }, [codprogprop]);

  const {
    data: {results = [], count = 0} = {},
    isError,
    isFetching,
    isLoading,
  } = useGetAllMonitorProgettiNodi(pagination, columnFilters, globalFilter, sorting);

  const renderNumberWithHighlight = (rawValue: string) => {
    // console.log(rawValue);
    const value = parseFloat(rawValue.replace(',', '.'));
    return (
        <div
            style={{
              backgroundColor: value < 0 ? '#ffe5e5' : 'transparent',
              padding: '2px 4px',
              borderRadius: '4px',
              textAlign: 'right',
            }}
        >
          {rawValue}
        </div>
    );
  };

  const columns = useMemo<MRT_ColumnDef<MonitorProgettiNodi>[]>(() => [
    {accessorKey: 'CODNODO', header: 'Codnodo', size: 60, maxSize: 80},
    {accessorKey: 'DESNODO', header: 'Desnodo', grow: true},
    {accessorKey: 'OREORDNODO', header: 'OreLav', size: 60, maxSize: 80},
    {accessorKey: 'ORECONT', header: 'OrePrep', size: 60, maxSize: 80},
    {accessorKey: 'OREPREV', header: 'OreStima', size: 60, maxSize: 80},
    {
      accessorKey: 'OREDIFF',
      header: 'OreDiff',
      size: 60,
      maxSize: 80,
      Cell: ({cell}) => renderNumberWithHighlight(cell.getValue<string>()),
    },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: results,
    rowCount: count,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableSorting: false,
    enablePagination: false,
    muiTableProps: {
      sx: {
        '& .MuiTableCell-root': {
          padding: '2px 6px',
          fontSize: '0.75rem',
        },
      },
    },
    muiTableBodyRowProps: ({row}) => ({
      onClick: () => setSelectedRow(row.original),
      sx: {
        height: '28px',
        cursor: 'pointer',
        backgroundColor: selectedRow?.CODNODO === row.original.CODNODO ? '#e0f7fa' : undefined,
      },
    }),
    muiTableHeadRowProps: {
      sx: {height: '30px'},
    },
    state: {
      isLoading,
      showProgressBars: isFetching,
      showAlertBanner: isError,
      columnFilters,
      globalFilter,
      pagination,
      sorting,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

return (
  <Box
    sx={{
      display: 'flex',
      width: '100%',       // fill parent
      // maxWidth: 1200,
      height: 500,         // fixed height for both
      fontSize: '0.75rem',
      transform: 'scale(0.9)',
      transformOrigin: 'top left',
      gap: 2,
    }}
  >
    {/* Left: table pane */}
    <Box
      sx={{
        flex: 1,           // takes 50%
        overflowY: 'auto',
        backgroundColor: '#fff',
        // borderRight: '1px solid #ccc', // optional visual separator
      }}
    >
      <MaterialReactTable table={table} />
    </Box>

    {/* Right: detail pane (always there, but empty if no row) */}
    <Box
      sx={{
        flex: 1,           // takes the other 50%
        overflowY: 'auto',
         }}
    >
      {selectedRow ? (
        <ListaMonitorProgettiVersOreInline
          codprogprop={selectedRow.CODPROG.trim()}
          codnodoprop={selectedRow.CODNODO.trim()}
        />
      ) : (
        // Optional: placeholder message when nothing’s selected
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#777',
            fontSize:  '1.3rem',
          }}
        >
          Seleziona un nodo per vedere i dettagli
        </Box>
      )}
    </Box>
  </Box>
);
}

export default ListaMonitorProgettiNodiInline;
